<template>
  <div ref="wordCounterRef" class="word-counter" @mousedown.prevent>
    <cv-tooltip alignment="center" direction="bottom" tip="" v-if="showTooltip">
      <svg-icon name="Info16" kind="indicative" />
    </cv-tooltip>
    <span @click="handleSwapCounterView"> {{ wordCountLabel }}</span>
  </div>
</template>

<script setup lang="ts">
import {
  countWords,
  countCharacters,
} from "@plugins/richtext/plugins/character-word-count/utils/character.word.count.utils";
import DOMUtils from "@utils/dom.utils";
import { ref, computed, onMounted, nextTick, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

// Props
interface Props {
  textContent: string;
  showTooltip: boolean;
  recommendedTooltipEnglishChar: number;
  recommendedTooltipChineseChar: number;
}

const props = defineProps<Props>();

const { t } = useI18n();
const wordCounterRef = useTemplateRef<HTMLElement>("wordCounterRef");

// Data
const showCharCount = ref(true);

// Computed
const charCount = computed(() => {
  return countCharacters(props.textContent);
});

const wordCount = computed(() => {
  return countWords(props.textContent);
});

const wordCountLabel = computed(() => {
  return showCharCount.value
    ? t("character_count_label", { charCount: charCount.value })
    : t("word_count_label", { wordCount: wordCount.value });
});

// Methods
const handleSwapCounterView = function () {
  showCharCount.value = !showCharCount.value;
};

const addWordCounterTooltipText = function () {
  const element = wordCounterRef.value;
  if (!element) return;

  const wordCounterTooltip = DOMUtils.elOf(element, ".word-counter .bx--assistive-text")[0];
  const title = t("char_word_count_tooltip_message");
  const englishRecommendedChar = t("char_word_count_tooltip_recommended_english_char", {
    charNo: props.recommendedTooltipEnglishChar,
  });

  if (wordCounterTooltip) {
    wordCounterTooltip.innerHTML = `
        <div>${title}</div> 
        <div>${englishRecommendedChar}</div>
      `;
  }
};

// Mounted
onMounted(() => {
  nextTick(() => {
    addWordCounterTooltipText();
  });
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.word-counter {
  @include carbon--type-style("label-01");
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: $interactive-04;

  .bx--tooltip__trigger.bx--tooltip--a11y {
    margin-right: $spacing-02;

    .bx--assistive-text {
      display: block;
      min-width: 19rem;
    }

    [stroke] {
      stroke: $interactive-01;
    }
  }

  span {
    cursor: pointer;
  }
}
</style>
